import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export interface NotificationPreferences {
  enabled: boolean;
  hour: number;
  minute: number;
  days: number[]; // 0-6, where 0 is Sunday
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  enabled: true,
  hour: 8,
  minute: 30,
  days: [1, 2, 3, 4, 5], // Monday through Friday
};

export const useNotificationPreferences = (userId: string | undefined) => {
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    loadPreferences();
  }, [userId]);

  const loadPreferences = async () => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('notification_preferences')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;

      if (data?.notification_preferences && typeof data.notification_preferences === 'object') {
        setPreferences({ ...DEFAULT_PREFERENCES, ...(data.notification_preferences as Partial<NotificationPreferences>) });
      }
    } catch (error) {
      logger.error('useNotificationPreferences:load', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreferences = async (newPreferences: Partial<NotificationPreferences>) => {
    if (!userId) return;

    const updated = { ...preferences, ...newPreferences };
    setPreferences(updated);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ notification_preferences: updated })
        .eq('id', userId);

      if (error) throw error;
    } catch (error) {
      logger.error('useNotificationPreferences:update', error);
      // Revert on error
      setPreferences(preferences);
    }
  };

  return {
    preferences,
    isLoading,
    updatePreferences,
  };
};
